import React, { useEffect, useCallback, useMemo } from "react";
import { ModalHeader, StyledModalBody } from "../../components/Modals";
import { Form, FormField } from "../../components/ui/Form";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Separator } from "../../components/ui/Separator";
import { Palette } from "../../components/utils";
import { Flex } from "../../components/ui/Flex";
import { FeatureSwitch } from "../offers/styles";
import { ButtonBodyVariant } from "../../components/ui/ButtonVariant";
import { useForm } from "react-hook-form";
import { 
  getBuildingThermostatPresets, 
  updateThermostatMasterControl,
  updateBuildingThermostatPresets,
  getMasterThermostatControl,
  attachThermostatPreset
} from "../../redux/actions/thermostat";
import { closeActiveModal } from "../../helper/modals";
import { setToast } from "../../redux/actions/window";
import { yupResolver } from "@hookform/resolvers/yup";  
import {
  SetPointRefreshFrequencyControl,
  SnapBackTemperatureControl,
  SubTitleText,
  TemperatureRangeControl,
  Text,
  ThermostatModal,
  TitleText,
} from "./ThermostatComponents";
import { createGlobalThermostatSchema } from "./ThermostatValidations";
import { LoadingSpinner } from "../../components/ui/Select";
import {
  GET_BUILDING_THERMOSTAT_PRESETS,
  UPDATE_BUILDING_THERMOSTAT_PRESETS,
  GET_MASTER_THERMOSTAT_CONTROL,
  UPDATE_MASTER_THERMOSTAT_CONTROL,
} from "../../redux/constants";
import { requestPending, requestSuccess, requestFail } from "../../utils/status";
import { defaultSetPoint, defaultSetPointCelsius, defaultTemperatureInputRangesCelsius, defaultTemperatureRange, defaultTemperatureRangeCelsius, defaultTemperatureInputRanges } from "../../utils/utils";

export const GlobalThermostatControlModal = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const modal = useSelector((state) => state.headerModal.modal);
  const {
    buildingPresets,
    buildingPresetsStatus,
    updatePresetsStatus,
    updateMasterControlStatus,
    masterThermostatControl,
    masterThermostatControlStatus,
    thermostatData,
  } = useSelector((state) => state.thermostat);
  const { globalOrgId } = useSelector((state) => state.Organization);
  const { globalBuildingId, globalSelectedBuilding, suites } = useSelector((state) => state.building);
  const unitsForDD = useSelector((state) => state.unit.unitsForDD);

  const organizationId = globalOrgId || localStorage.getItem("orgId");
  const buildingId = globalBuildingId || localStorage.getItem("building_id");

  const buildingTempUnit = globalSelectedBuilding?.config?.thermostats_temp_unit || 'F';
  const occupiedPreset = buildingPresets?.find(item => item?.type === "occupied");
  const vacantPreset = buildingPresets?.find(item => item?.type === "vacant");

  const isSuiteEnabled = globalSelectedBuilding?.config?.is_suite_enabled || false;

  const unitDataMap = useMemo(() => {
    const sourceData = isSuiteEnabled ? suites : unitsForDD;
    if (!sourceData) return {};

    return sourceData.reduce((acc, room) => {
      acc[room.id] = {
        title: isSuiteEnabled ? room.name : room.unit_num,
        isVacant: room.is_vacant, 
        reservations: room.reservations || [] 
      };
      return acc;
    }, {});
  }, [isSuiteEnabled, suites, unitsForDD]);


  const isLoadingPresets = buildingPresetsStatus === requestPending(GET_BUILDING_THERMOSTAT_PRESETS);
  const isLoadingMasterControl = masterThermostatControlStatus === requestPending(GET_MASTER_THERMOSTAT_CONTROL);
  const isUpdatingPresets = updatePresetsStatus === requestPending(UPDATE_BUILDING_THERMOSTAT_PRESETS);
  const isUpdatingMasterControl = updateMasterControlStatus === requestPending(UPDATE_MASTER_THERMOSTAT_CONTROL);
  const hasPresetsLoaded = buildingPresetsStatus === requestSuccess(GET_BUILDING_THERMOSTAT_PRESETS);
  const hasPresetsError = buildingPresetsStatus === requestFail(GET_BUILDING_THERMOSTAT_PRESETS);

  const form = useForm({
    resolver: yupResolver(createGlobalThermostatSchema(buildingTempUnit)),
    mode: "onSubmit",
    defaultValues: {
      buildingThermostatMasterControl: false,
      enableOccupiedSnapBack: false,
      occupiedSnapBackTemperature: buildingTempUnit === 'C' ? defaultSetPointCelsius.occupied : defaultSetPoint.occupied,
      occupiedSnapBackTemperatureAdjustment: 3,
      occupiedHeatTemperatureRange: buildingTempUnit === 'C' ? defaultTemperatureInputRangesCelsius.heat : defaultTemperatureInputRanges.heat,
      occupiedCoolTemperatureRange: buildingTempUnit === 'C' ? defaultTemperatureInputRangesCelsius.cool : defaultTemperatureInputRanges.cool,
      enableVacantSnapBack: false,
      vacantSnapBackTemperature: buildingTempUnit === 'C' ? defaultSetPointCelsius.vacant : defaultSetPoint.vacant,
      vacantSnapBackTemperatureAdjustment: 3,
      vacantHeatTemperatureRange: buildingTempUnit === 'C' ? defaultTemperatureInputRangesCelsius.heat : defaultTemperatureInputRanges.heat,
      vacantCoolTemperatureRange: buildingTempUnit === 'C' ? defaultTemperatureInputRangesCelsius.cool : defaultTemperatureInputRanges.cool,
    },
  });

  const enableOccupiedSnapBack = form.watch("enableOccupiedSnapBack");
  const enableVacantSnapBack = form.watch("enableVacantSnapBack");
  const buildingThermostatMasterControl = form.watch("buildingThermostatMasterControl");

  // Update form when master control state is loaded from API
  useEffect(() => {
    if (masterThermostatControl && modal === "GlobalThermostatControlModal") {
      form.setValue("buildingThermostatMasterControl", masterThermostatControl.is_active || false);
    }
  }, [masterThermostatControl, modal, form]);

  useEffect(() => {
    if (modal !== "GlobalThermostatControlModal" || !organizationId || !buildingId) {
      return;
    }

    const fetchPresets = () => {
      dispatch(
        getMasterThermostatControl({
          organizationId: parseInt(organizationId),
          buildingId: parseInt(buildingId),
        })
      );
      
      dispatch(
        getBuildingThermostatPresets({
          organizationId: parseInt(organizationId),
          buildingId: parseInt(buildingId),
        })
      );
    };
    fetchPresets();
  }, [dispatch, organizationId, buildingId, modal]);

  useEffect(() => {
    if (hasPresetsLoaded && buildingPresets?.length > 0) {
      try {
        const setPresetValues = (prefix, preset) => {
          if (!preset) return;

          let defaultHeatRange = defaultTemperatureRange.heat;
          let defaultCoolRange = defaultTemperatureRange.cool;
          let defaultSnapBackTemp = prefix === "occupied" ? defaultSetPoint.occupied : defaultSetPoint.vacant;

          if(buildingTempUnit === 'C') {
            defaultHeatRange = defaultTemperatureRangeCelsius.heat;
            defaultCoolRange = defaultTemperatureRangeCelsius.cool;
            defaultSnapBackTemp = prefix === "occupied" ? defaultSetPointCelsius.occupied : defaultSetPointCelsius.vacant;
          }

          const snapBackTemp = Number(preset.snapback_setpoint) || defaultSnapBackTemp;
          const heatRange = [
            Number(preset.min_heat) || defaultHeatRange[0],
            Number(preset.max_heat) || defaultHeatRange[1],
          ];
          const coolRange = [
            Number(preset.min_cool) || defaultCoolRange[0],
            Number(preset.max_cool) || defaultCoolRange[1],
          ];

          form.setValue(
            `enable${prefix.charAt(0).toUpperCase() + prefix.slice(1)}SnapBack`,
            preset.snapback_enabled || false
          );
          form.setValue(
            `${prefix}SnapBackTemperature`,
            snapBackTemp
          );
          form.setValue(
            `${prefix}SnapBackTemperatureAdjustment`,preset.snapback_refresh_frequency_hours
          );
          form.setValue(`${prefix}HeatTemperatureRange`, heatRange);
          form.setValue(`${prefix}CoolTemperatureRange`, coolRange);
        };
        setPresetValues("occupied", occupiedPreset);
        setPresetValues("vacant", vacantPreset);
      } catch (error) {
        console.error("Error setting form values:", error);
        dispatch(setToast({ toastShow: true, toastMessage: t("Error loading thermostat settings") }));
      }
    }
  }, [hasPresetsLoaded, buildingPresets, form, occupiedPreset, vacantPreset, dispatch, t, buildingTempUnit]);

  const handleMasterControlToggle = useCallback(
    (enabled) => {
      form.setValue("buildingThermostatMasterControl", enabled);
    },
    [form]
  );

  const attachPresetsToThermostats = useCallback(async (updatedBuildingPresets) => {
    if (!updatedBuildingPresets || !unitDataMap || Object.keys(unitDataMap).length === 0 || !thermostatData) {
      console.log('No updated building presets, unit data map, or thermostat data available for preset attachment');
      return;
    }

    const attachPresetPromises = [];

    thermostatData.forEach((thermostat) => {
      const thermostatPresetId = parseInt(thermostat.preset_id) ?? 0;
      
      if (thermostatPresetId === 0) {
        const id = isSuiteEnabled ? thermostat.suiteId : thermostat.unitId;
        const unitData = unitDataMap && unitDataMap[id] ? unitDataMap[id] : null;
        
        if (unitData) {
          const isVacant = unitData.isVacant ?? false;
          const presetType = isVacant ? 'vacant' : 'occupied';
          const currentPreset = updatedBuildingPresets.find(preset => preset?.type === presetType);
          
          if (currentPreset) {
            const promise = new Promise((resolve, reject) => {
              dispatch(attachThermostatPreset({
                presetId: currentPreset.id,
                body: {
                  orgId: parseInt(organizationId),
                  buildingId: parseInt(buildingId),
                  deviceIds: [thermostat.id],
                },
                success: (response) => {
                  resolve();
                },
                fail: (error) => {
                  console.error(`Failed to attach ${presetType} preset to thermostat ${thermostat.id}:`, error);
                  reject(error);
                }
              }));
            });
            attachPresetPromises.push(promise);
          } else {
            console.warn(`No ${presetType} preset found in updated presets for thermostat ${thermostat.id}`);
          }
        } else {
          console.warn(`No unit data found for thermostat ${thermostat.id}`);
        }
      }
    });

    if (attachPresetPromises.length > 0) {
      try {
        await Promise.all(attachPresetPromises);
      } catch (error) {
        console.error('Error attaching presets to thermostats:', error);
      }
    }
  }, [thermostatData, unitDataMap, isSuiteEnabled, organizationId, buildingId, dispatch]);

  const onSubmit = async (values) => {
    try {
      await dispatch(
        updateThermostatMasterControl({
          organizationId: parseInt(organizationId),
          buildingId: parseInt(buildingId),
          body: {
            is_active: values.buildingThermostatMasterControl,
          },
          success: async () => {
            if (!values.buildingThermostatMasterControl) {
              dispatch(setToast({ toastShow: true, toastMessage: t("Master control has been disabled") }));
              return;
            }

            const presetsToUpdate = [];

            const occupiedSnapBackTemp = values.occupiedSnapBackTemperature;
            const occupiedHeatRange = values.occupiedHeatTemperatureRange;
            const occupiedCoolRange = values.occupiedCoolTemperatureRange;

            presetsToUpdate.push({
              id: occupiedPreset?.id || null,
              type: occupiedPreset?.type || "occupied",
              snapback_enabled: values.enableOccupiedSnapBack ?? false,
              snapback_setpoint: occupiedSnapBackTemp,
              snapback_refresh_frequency_hours: values.occupiedSnapBackTemperatureAdjustment,
              min_heat: occupiedHeatRange[0],
              max_heat: occupiedHeatRange[1],
              min_cool: occupiedCoolRange[0],
              max_cool: occupiedCoolRange[1],
            });

            // Handle vacant preset - create if doesn't exist, update if it does
            const vacantSnapBackTemp = values.vacantSnapBackTemperature;
            const vacantHeatRange = values.vacantHeatTemperatureRange;
            const vacantCoolRange = values.vacantCoolTemperatureRange;

            presetsToUpdate.push({
              id: vacantPreset?.id || null,
              type: vacantPreset?.type || "vacant",
              snapback_enabled: values.enableVacantSnapBack ?? false,
              snapback_setpoint: vacantSnapBackTemp,
              snapback_refresh_frequency_hours: values.vacantSnapBackTemperatureAdjustment,
              min_heat: vacantHeatRange[0],
              max_heat: vacantHeatRange[1],
              min_cool: vacantCoolRange[0],
              max_cool: vacantCoolRange[1],
            });


            if (presetsToUpdate.length === 0) {
              dispatch(setToast({ toastShow: true, toastMessage: t("Master control has been enabled") }));
              return;
            }

            try {
              await dispatch(
                updateBuildingThermostatPresets({
                  presetId: occupiedPreset?.id || null,
                  organizationId: parseInt(organizationId),
                  buildingId: parseInt(buildingId),
                  body: presetsToUpdate,
                  success: async ({data}) => {
                    if (thermostatData && thermostatData.length > 0) {
                      await attachPresetsToThermostats(data);
                    }
                  }
                })
              );

              dispatch(setToast({ toastShow: true, toastMessage: t("Settings have been successfully updated") }));
            } catch (presetError) {
              console.error("Error updating thermostat presets:", presetError);
              dispatch(setToast({ toastShow: true, toastMessage: t("Error updating thermostat presets") }));
            }
          },
          fail: () => {
            dispatch(setToast({ toastShow: true, toastMessage: t("Error updating master control") }));
          }
        })
      );

      closeActiveModal();

    } catch (error) {
      console.error("Error updating thermostat settings:", error);
      dispatch(setToast({ toastShow: true, toastMessage: t("Error updating settings") }));
    }
  };

  if (isLoadingPresets || isLoadingMasterControl) {
    return (
      <ThermostatModal show={modal === "GlobalThermostatControlModal"} side={true} closeOnBackdrop={false}>
        <div style={{ padding: "1.5rem" }}>
          <ModalHeader title={t("Global Settings")} />
        </div>
        <Separator color={Palette.BLACK_20} orientation="horizontal" />
        <StyledModalBody>
          <Flex justify="center" align="center" style={{ marginTop: "100px", height: "100%", minHeight: "400px", width: "100%" }}>
            <LoadingSpinner size={24} />
          </Flex>
        </StyledModalBody>
      </ThermostatModal>
    );
  }

  if (hasPresetsError) {
    return (
      <ThermostatModal show={modal === "GlobalThermostatControlModal"} side={true} closeOnBackdrop={false}>
        <div style={{ padding: "1.5rem" }}>
          <ModalHeader title={t("Global Settings")} />
        </div>
        <Separator color={Palette.BLACK_20} orientation="horizontal" />
        <StyledModalBody>
          <Flex justify="center" align="center" style={{ height: "100%", minHeight: "400px", width: "100%" }}>
            <Text>{t("Error loading thermostat settings. Please try again.")}</Text>
          </Flex>
        </StyledModalBody>
      </ThermostatModal>
    );
  }

  return (
    <ThermostatModal
      show={modal === "GlobalThermostatControlModal"}
      side={true}
      closeOnBackdrop={false}
      disableClose={isUpdatingPresets || isUpdatingMasterControl}
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div style={{ padding: "1.5rem" }}>
            <ModalHeader title={t("Global Settings")} />
          </div>

          <Separator color={Palette.BLACK_20} orientation="horizontal" />

          <StyledModalBody>
            <Flex gap="1rem" direction="column" style={{ padding: "1.5rem", flex: 1 }}>
              <Flex gap="3rem" justify="space-between">
                <Flex direction="column" gap="4px">
                  <SubTitleText>Building Thermostat Master Control</SubTitleText>
                  <Text>
                    Turning off the Master Control will allow residents to make adjustments to their own thermostats.
                  </Text>
                </Flex>
                <FeatureSwitch
                    onChange={() => handleMasterControlToggle(!buildingThermostatMasterControl)}
                    checked={buildingThermostatMasterControl || false}
                    value={buildingThermostatMasterControl || false}
                    shape="pill"
                    color="primary"
                    disabled={isUpdatingMasterControl}
                />
              </Flex>

              {isUpdatingMasterControl ? (
                <Flex justify="center" align="center" style={{ marginTop: "100px", height: "100%", minHeight: "200px", width: "100%" }} aria-label="Loading thermostat settings">
                  <LoadingSpinner size={24} />
                </Flex> 
              ) : (              
              form.watch("buildingThermostatMasterControl") && (
                <>
                  <Separator color={Palette.BLACK_20} orientation="horizontal" />
                  <Flex gap="0.5rem" justify="space-between" direction="column">
                    <TitleText>Occupied Preset</TitleText>
                    <Flex gap="3rem" justify="space-between">
                      <Flex direction="column" gap="4px">
                        <SubTitleText>Snap Back Temperature</SubTitleText>
                        <Text>
                          Automatically returns the thermostat to a preset temperature range after a period of inactivity
                          or manual override.
                        </Text>
                      </Flex>
                      <FormField
                        control={form.control}
                        name="enableOccupiedSnapBack" 
                        render={({ field: { onChange, value } }) => (
                          <FeatureSwitch
                            onChange={onChange}
                            checked={value || false}
                            value={value || false}
                            shape="pill"
                            color="primary"
                            disabled={isUpdatingPresets}
                          />
                        )}
                      />
                    </Flex>
                  </Flex>

                  {enableOccupiedSnapBack && (
                    <>
                      <SnapBackTemperatureControl 
                        name="occupiedSnapBackTemperature" 
                        control={form.control} 
                        buildingTempUnit={buildingTempUnit}
                      />
                      <SetPointRefreshFrequencyControl
                        name="occupiedSnapBackTemperatureAdjustment"
                        control={form.control}
                      />
                    </>
                  )}

                  <Flex direction="column" gap="0.5rem" style={{ width: "100%" }}>
                    <SubTitleText>Min & Max Set Temperature</SubTitleText>
                    <TemperatureRangeControl
                      name="occupiedHeatTemperatureRange"
                      label="Heat"
                      minMax={buildingTempUnit === 'C' ? defaultTemperatureRangeCelsius.heat : defaultTemperatureRange.heat} 
                      control={form.control}
                      buildingTempUnit={buildingTempUnit}
                    />
                    <TemperatureRangeControl
                      name="occupiedCoolTemperatureRange"
                      label="Cool"
                      minMax={buildingTempUnit === 'C' ? defaultTemperatureRangeCelsius.cool : defaultTemperatureRange.cool}
                      control={form.control}
                      buildingTempUnit={buildingTempUnit}
                    />
                  </Flex>

                  <Separator color={Palette.BLACK_20} orientation="horizontal" />

                  <Flex gap="0.5rem" justify="space-between" direction="column">
                    <TitleText>Vacant Preset</TitleText>
                    <Flex gap="3rem" justify="space-between">
                      <Flex direction="column" gap="4px">
                        <SubTitleText>Snap Back Temperature</SubTitleText>
                        <Text>
                          Automatically returns the thermostat to a preset temperature range after a period of inactivity
                          or manual override.
                        </Text>
                      </Flex>
                      <FormField
                        control={form.control}
                        name="enableVacantSnapBack"
                        render={({ field: { onChange, value } }) => (
                          <FeatureSwitch
                            onChange={onChange}
                            checked={value || false}
                            value={value || false}
                            shape="pill"
                            color="primary"
                            disabled={isUpdatingPresets}
                          />
                        )}
                      />
                    </Flex>
                  </Flex>

                  {enableVacantSnapBack && (
                    <>
                      <SnapBackTemperatureControl 
                        name="vacantSnapBackTemperature" 
                        control={form.control} 
                        buildingTempUnit={buildingTempUnit}
                      />
                      <SetPointRefreshFrequencyControl
                        name="vacantSnapBackTemperatureAdjustment"
                        control={form.control}
                      />
                    </>
                  )}

                  <Separator color={Palette.BLACK_20} orientation="horizontal" />

                  <Flex direction="column" gap="0.5rem" style={{ width: "100%" }}>
                    <SubTitleText>Min & Max Set Temperature</SubTitleText>
                    <TemperatureRangeControl
                      name="vacantHeatTemperatureRange"
                      label="Heat"
                      minMax={buildingTempUnit === 'C' ? defaultTemperatureRangeCelsius.heat : defaultTemperatureRange.heat}
                      control={form.control}
                      buildingTempUnit={buildingTempUnit}
                    />
                    <TemperatureRangeControl
                      name="vacantCoolTemperatureRange"
                      label="Cool"
                      minMax={buildingTempUnit === 'C' ? defaultTemperatureRangeCelsius.cool : defaultTemperatureRange.cool}
                      control={form.control}
                      buildingTempUnit={buildingTempUnit}
                    />
                  </Flex>
                </>
              ))}
            </Flex>
          </StyledModalBody>
          
          {!isUpdatingMasterControl &&
            <div style={{ padding: "1.5rem" }}>
              <ButtonBodyVariant
                style={{ width: "100%" }}
                type="submit"
                disabled={isUpdatingPresets || isUpdatingMasterControl}
              >
                {isUpdatingPresets || isUpdatingMasterControl ? t("Saving...") : t("Update")}
              </ButtonBodyVariant>
            </div>
          }
        </form>
      </Form>
    </ThermostatModal>
  );
};